import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { generateMvpCardNumbers } from './holders';

/** Download unbound card numbers for pre-printed MVP card stock. Access is checked by its caller. */
export function MvpCardNumberGenerator() {
  const { t } = useTranslation('common');
  const { venueId } = useVenueContext();
  const [cardCount, setCardCount] = useState(100);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(false);

  async function downloadCardNumbers(event: FormEvent) {
    event.preventDefault();
    setGenerateError(false);
    setGenerating(true);
    try {
      const numbers = await generateMvpCardNumbers(venueId, cardCount);
      const url = URL.createObjectURL(
        new Blob([numbers.join('\r\n') + '\r\n'], { type: 'text/csv' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = 'mvp-card-numbers.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setGenerateError(true);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <form className="flex flex-wrap items-end gap-2" onSubmit={downloadCardNumbers}>
      <label className="grid gap-1 text-sm">
        {t('mvpCustomers.generate.count')}
        <Input
          type="number"
          min={1}
          max={10000}
          required
          className="w-32"
          value={cardCount}
          onChange={(event) => setCardCount(Number(event.target.value))}
        />
      </label>
      <Button type="submit" disabled={generating || cardCount < 1 || cardCount > 10000}>
        {generating ? t('mvpCustomers.generate.generating') : t('mvpCustomers.generate.download')}
      </Button>
      {generateError && (
        <p role="alert" className="text-sm text-destructive">
          {t('mvpCustomers.generate.failed')}
        </p>
      )}
    </form>
  );
}
